import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import RatingStars from "./RatingStars";
import ReviewOptionGroup from "./ReviewOptionGroup";
import EntityInfoGrid from "./EntityInfoGrid";
import {formatDate} from "../../../utils/jalaliDate"

import {
  getReviewOptions,
  createCustomerReview,
  updateCustomerReview,
} from "../../../services/reviewService";
import {FiTruck, FiUserCheck} from "react-icons/fi";
import Button from "../../Button";

const pick = (...values) => {
  return values.find(
    (value) => value !== undefined && value !== null && value !== ""
  );
};

const buildEntityInfoItems = (entity) => {
  if (!entity) return [];

  if (Array.isArray(entity.infoItems)) {
    return entity.infoItems;
  }

  const entityType = entity.entityType || entity.entity_type;
  const shippingLabels = {
    carrier: { label: "ارسال شرکت حمل", icon: <FiTruck className="w-5 h-5" /> },
    supplier: { label: "ارسال تأمین‌کننده", icon: <FiUserCheck className="w-5 h-5" /> },
    pickup: { label: "تحویل حضوری", icon: <FiUserCheck className="w-5 h-5" /> },
  };

  const UNIT_TRANSLATIONS = {
    'count': 'عدد',
    'package': 'بسته',
    'weight': 'کیلو گرم',
    'length': 'متر',
    'area': 'متر مربع',
    'litr': 'لیتر',
    'volume': 'متر مکعب'
    };
  const info = entity.info || {};

  if (entityType === "shipment") {
    return [
      {
        label: "کد محموله",
        value: pick(info.shipment_code?.slice(0, 8)),
      },
      {
        label: "زمان ثبت سفارش",
        value: pick(formatDate(info.created_at)),
      },
      {
        label: "زمان تحویل",
        value: pick(formatDate(info.delivered_at)),
      },
    {
        label: "تأمین کننده",
        value: pick(info.supplier_name),
      }
    ].filter((item) => item.value);
  }

  if (entityType === "shipment_item") {
    return [
      {
        label: "نام کالا",
        value: pick(info.product_name),
      },
      {
        label: "دسته کالا",
        value: pick(info.product_category),
      },
      {
        label: "برند کالا",
        value: pick(info.product_brand),
      },
      {
        label: "تعداد",
        value: pick(info.qty_ordered),
      }
    ].filter((item) => item.value);
  }

  return [];
};

const buildEntityImages = (entity) => {
  if (!entity) return [];

  const info = entity.info || {};

  const images = [
    info.product_image,
    info.image,
    info.thumbnail,
  ].filter(Boolean);

  return images;
};

const CustomerReviewModal = ({
  open,
  onClose,
  review,
  entity,
}) => {

  const queryClient = useQueryClient();

  const entityType = entity?.entityType || entity?.entity_type;
  const isEditMode = Boolean(review?.id);

  const [isDisplayed, setIsDisplayed] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);

  const ratingLabels = {
    1: "خیلی ضعیف",
    2: "ضعیف",
    3: "متوسط",
    4: "خوب",
    5: "عالی",
  };


  const entityInfoItems = useMemo(
    () => buildEntityInfoItems(entity),
    [entity]
  );

  const entityImages = useMemo(
    () => buildEntityImages(entity),
    [entity]
  );

  const { data: optionsData, isLoading } = useQuery({
    queryKey: ["review-options", entityType],
    queryFn: () => getReviewOptions(entityType),
    enabled: open && !!entityType,
    staleTime: 1000 * 60 * 10,
  });

  const options = useMemo(() => {
    if (Array.isArray(optionsData)) return optionsData;
    if (Array.isArray(optionsData?.results)) return optionsData.results;
    return [];
  }, [optionsData]);

  const positiveOptions = options.filter(
    (o) => o.sentiment === "positive"
  );

  const negativeOptions = options.filter(
    (o) => o.sentiment === "negative"
  );

  useEffect(() => {
    if (!open) return;

    setRating(review?.rating || 0);
    setComment(review?.comment || "");
    setIsDisplayed(review?.is_displayed ?? true);

    const ids =
      review?.selected_options?.map((o) => o.id) ||
      review?.selected_option_ids ||
      [];

    setSelectedOptionIds(ids);

  }, [open, review]);

  const toggleOption = (optionId) => {
    setSelectedOptionIds((prev) => {
      if (prev.includes(optionId)) {
        return prev.filter((id) => id !== optionId);
      }

      return [...prev, optionId];
    });
  };

  const buildPayload = () => {

    const payload = {
      entity_type: entityType,
      rating,
      comment,
      is_displayed: isDisplayed,
      selected_option_ids: selectedOptionIds,
    };


    payload.shipment = entity?.shipmentId;

    if (entityType === "shipment_item") {
      payload.shipment_item = entity?.shipmentItemId;
    }

    return payload;
  };

  const mutation = useMutation({
    mutationFn: () => {

      const payload = buildPayload();

      if (isEditMode) {
        return updateCustomerReview(review.id, payload);
      }

      return createCustomerReview(payload);
    },

    onSuccess: () => {

      queryClient.invalidateQueries({
        queryKey: ["orders"],
      });

      queryClient.invalidateQueries({
        queryKey: ["orderReviewables"],
      });

      onClose?.();
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!rating) {
      alert("لطفاً امتیاز ثبت کنید");
      return;
    }

    mutation.mutate();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">

      <div className="bg-white w-full max-w-5xl rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">

        <form onSubmit={handleSubmit} className="p-8">

          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <h2 className="text-xl font-bold">
              {isEditMode ? "ویرایش نظر" : "ثبت نظر"}
            </h2>

            <button
                type="button"
                onClick={onClose}
                className="text-xl text-gray-500"
            >
              ×
            </button>
          </div>

          {entityInfoItems.length > 0 && (
              <EntityInfoGrid items={entityInfoItems}/>
          )}

          {entityImages.length > 0 && (
              <div className="flex gap-3 mt-6">
                {entityImages.map((img, i) => (
                    <img
                        key={i}
                        src={img}
                        alt=""
                        className="w-16 h-16 rounded object-cover border"
                    />
                ))}
              </div>
          )}

          <div className="mt-10 flex flex-col items-center">
            <p className="mb-3 text-sm">
              امتیاز شما :
              {rating > 0 && (
                  <span className="text-gray-600 font-medium">
            {ratingLabels[rating]}
          </span>
              )}
            </p>

            <RatingStars
                value={rating}
                onChange={setRating}
            />
          </div>

          <div className="mt-10">

            {isLoading ? (
                <div className="text-center text-gray-400">
                  در حال دریافت گزینه ها...
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-8">

                  <ReviewOptionGroup
                      title="نقاط قوت"
                      options={positiveOptions}
                      variant="positive"
                      selectedIds={selectedOptionIds}
                      onToggle={toggleOption}
                  />

                  <ReviewOptionGroup
                      title="نقاط ضعف"
                      options={negativeOptions}
                      variant="negative"
                      selectedIds={selectedOptionIds}
                      onToggle={toggleOption}
                  />

                </div>
            )}

          </div>

          <div className="mt-8">
            <label className="text-sm block mb-2">
              متن نظر
            </label>

            <textarea
                rows={5}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full border rounded p-3 text-sm"
                placeholder="نظر خود را بنویسید"
            />
          </div>

          <div className="mt-6 flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-700">
                نمایش مشخصات در لیست نظرات کالا
              </span>

              <button
                  type="button"
                  onClick={() => setIsDisplayed((prev) => !prev)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      isDisplayed ? "bg-green-500" : "bg-gray-300"
                  }`}
              >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isDisplayed ? "-translate-x-6" : "-translate-x-1"
                }`}
            />
              </button>
            </div>

            <div className="flex gap-2 justify-start">
              <Button
                  variant="primary"
                  type="submit"
                  disabled={mutation.isPending}
              >
                {mutation.isPending ? "در حال ثبت..." : "ثبت نظر"}
              </Button>

              <Button
                  variant="secondary"
                  type="button"
                  onClick={onClose}
              >
                انصراف
              </Button>
            </div>
          </div>


        </form>

      </div>

    </div>
  );
};

export default CustomerReviewModal;
